import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Dashboard.module.css';
import Form from './form/Form';
import FormGroup from './form-group/FormGroup';
import pageData from './form-data';
import { getPageIdByName } from '../../../_metronic/helpers/utils/api';
import { useLanguage } from '../../../_metronic/helpers/utils/context';
import { convertToNormalEnglish } from '../../../_metronic/helpers/utils/utils';
import TemplatePageEditor from './TemplatePageEditor';

const Dashboard = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const [pageName, section] = pathParts.slice(2, 4);
  const templatePageId = pathParts[4];
  const sectionName = section ?? pageName;
  const formData = pageData[`${pageName}Data`]?.[`${sectionName}Data`];
  const [pageId, setPageId] = useState<number | null>(null);

  useEffect(() => {
    const fetchPageId = async () => {
      const id = await getPageIdByName(pageName);
      setPageId(id);
    };

    fetchPageId();
  }, [pageName]);

  const getTitle = () => {
    const isPageAndSectionSame = pageName.toLowerCase() === sectionName.toLowerCase();
    const title = isPageAndSectionSame ? pageName : `${pageName} ${sectionName}`;
    return convertToNormalEnglish(title);
  };

  if (pageName === 'template' && section === 'template' && templatePageId) {
    return <TemplatePageEditor pageId={templatePageId} />;
  }

  if (!pageId || !formData) {
    return null;
  }

  return (
    <main className={styles.container}>
      <h1 className={styles.title}>{getTitle()}</h1>

      {pageName === 'blog' && (
        <div style={{
          background: 'linear-gradient(135deg, #FFF8E1 0%, #FFF3CD 100%)',
          border: '1px solid #FFE082',
          borderRadius: '16px',
          padding: '28px 32px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '20px',
          boxShadow: '0 4px 16px rgba(255, 193, 7, 0.12)',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: '#FFC107',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <i className="bi bi-info-circle-fill" style={{ color: '#fff', fontSize: '22px' }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <h4 style={{ fontWeight: 700, color: '#664D03', marginBottom: '8px', fontSize: '16px' }}>
              Blog &amp; Stories are managed on the IPF Admin Platform
            </h4>
            <p style={{ color: '#856404', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
              To create, edit, or publish new blog posts and success stories, head over to the 
              <strong> IPF Admin Panel</strong>. Once logged in, navigate to <strong>News</strong> in 
              the left sidebar to manage all your content. The settings below only control the 
              appearance of the blog listing page on the public website.
            </p>
            <a
              href="https://admin.indigenousprosperityfoundation.ca/"
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '16px',
                padding: '10px 24px',
                backgroundColor: '#121E5C',
                color: '#fff',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '14px',
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(18, 30, 92, 0.25)',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                (e.target as HTMLElement).style.boxShadow = '0 6px 20px rgba(18, 30, 92, 0.35)';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.transform = 'translateY(0)';
                (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(18, 30, 92, 0.25)';
              }}
            >
              <i className="bi bi-box-arrow-up-right" style={{ fontSize: '14px' }}></i>
              Open IPF Admin Panel
            </a>
          </div>
        </div>
      )}
      {pageName !== 'blog' && formData.map((item) =>
        item.formId.startsWith('formGroup') ? (
          <FormGroup
            key={`${pageName}-${sectionName}-${item.formId}-${language}`}
            formId={item.formId}
            inputData={item.data}
            nestedData={item.nested}
            heading={item.heading}
            pageId={pageId}
            sectionName={sectionName}
          />
        ) : (
          <Form
            key={`${pageName}-${sectionName}-${item.formId}-${language}`}
            formId={item.formId}
            inputData={item.data}
            heading={item.heading}
            pageId={pageId}
            sectionName={sectionName}
          />
        )
      )}
    </main>
  );
};

export default Dashboard;
